import React from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import ArrowUpIcon from "@atlaskit/icon/core/arrow-up";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { getFirstName } from "@/components/signup/utils";
import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { RovoIcon } from "../../../../../search/search-page/src/components/icons/rovo-icon";

import { AIFooter } from "./components/ai-footer";
import { ColdStartButtonsRow } from "./components/cold-starts-buttons";
import { RovoIconButton } from "./rovo-icon-button";

const sectionStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});
const chatHeaderContainerStyles = css({
    maxWidth: "768px",
    width: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    marginBlockEnd: "24px"
});

const rovoIconStyles = css({
    width: "36px",
    height: "36px"
});

const h1Styles = css({
    padding: 0,
    margin: 0,
    lineHeight: "56px",
    fontSize: "48px",
    fontWeight: "800"
});

const textfieldContainerStyles = css({
    display: "flex",
    maxWidth: "700px",
    width: "100%",
    flexDirection: "column",
    alignItems: "stretch",
    gap: token("space.150"),
    boxSizing: "border-box",

    borderRadius: "12px",
    border: `1px solid ${wacToken("color.neutral.light300")}`,
    background: token("color.background.input"),
    boxShadow: token("elevation.shadow.overlay"),
    padding: `${token("space.200")} ${token("space.200")} ${token(
        "space.150"
    )} ${token("space.200")}`,
    [media.below.xs]: {
        padding: `${token("space.150")}`
    },
    overflow: "hidden"
});

const ctaButtonStyles = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    width: "100%",
    gap: token("space.100")
});

type RovoPillButtonProps = {
    isDisabled?: boolean;
    onClick?: () => void;
};

const disabledInteractionCss = css({
    pointerEvents: "none",
    cursor: "default"
});

const RovoPillButton: React.FC<RovoPillButtonProps> = ({
    isDisabled,
    onClick
}) => {
    const intl = useIntl();
    const askRovoLabel = intl.formatMessage({
        id: "RovoChat.askRovo",
        defaultMessage: "Ask Rovo"
    });

    return (
        <Button
            css={[
                css({ minHeight: "40px" }),
                isDisabled ? disabledInteractionCss : null
            ]}
            variant="primary"
            size="small"
            onClick={isDisabled ? undefined : onClick}
        >
            {askRovoLabel}
        </Button>
    );
};

const inputStyles = css({
    "width": "100%",
    "minHeight": "20px",
    "maxHeight": "140px",
    "overflowY": "auto",
    "border": "none",
    "background": "transparent",
    "padding": 0,
    "fontSize": "16px",
    "fontFamily": "inherit",
    "lineHeight": "1.5",
    "wordWrap": "break-word",
    "overflowWrap": "break-word",
    "whiteSpace": "pre-wrap",
    "resize": "none",
    "&:focus": {
        outline: "none"
    }
});
const chatBoxContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "700px",
    width: "100%",
    gap: "8px"
});

const getNamedHeader = (firstName: string) => {
    return (
        <FormattedMessage
            id="RovoChat.greeting"
            defaultMessage="Hey {name},{br}let's get to work"
            values={{
                name: getFirstName(firstName),
                br: <br />
            }}
        />
    );
};
export const ChatHeader: React.FC = () => {
    const { isUserLoggedIn, name } = useLoggedInUser();
    const firstName = getFirstName(name ?? "");

    // If 'gwp-9000-new-rovo-video-hero' is enabled, <h1> shows the video; else, Rovo Chat Header uses <h1>.
    // Check feature flag at render time to allow tests to mock it properly
    const isNewRovoVideoHeroEnabled = checkFeatureFlagGate(
        featureFlags.gwp9000NewRovoVideoHero
    );

    const getMessage = () => {
        if (isUserLoggedIn && firstName) {
            return getNamedHeader(firstName);
        }
        return (
            <FormattedMessage
                id="RovoChat.greeting"
                defaultMessage="Hey there,{br}ready to get to work?"
                values={{
                    br: <br />
                }}
            />
        );
    };

    return (
        <div css={chatHeaderContainerStyles}>
            <RovoIcon css={rovoIconStyles} />
            {isNewRovoVideoHeroEnabled ? (
                <h2 css={h1Styles}>{getMessage()}</h2>
            ) : (
                <h1 css={h1Styles}>{getMessage()}</h1>
            )}
        </div>
    );
};

type RovoChatBoxProps = {
    textareaRef?: React.Ref<HTMLTextAreaElement>;
    value?: string;
    onValueChange?: (value: string) => void;
};

export const RovoChatBox: React.FC<RovoChatBoxProps> = ({
    textareaRef: externalRef,
    value: externalValue,
    onValueChange
}) => {
    const intl = useIntl();
    const placeholder = intl.formatMessage({
        id: "RovoChat.placeholder",
        defaultMessage: "Find something, plan something, or make something"
    });

    const internalRef = React.useRef<HTMLTextAreaElement>(null);
    const textareaRef = externalRef || internalRef;
    const MAX_HEIGHT = 140; // keep in sync with inputStyles.maxHeight

    const resizeToContent = React.useCallback(() => {
        const el = (textareaRef as React.RefObject<HTMLTextAreaElement>)
            .current;
        if (!el) return;
        el.style.height = "auto";
        const next = Math.min(el.scrollHeight, MAX_HEIGHT);
        el.style.height = `${next}px`;
    }, [textareaRef, MAX_HEIGHT]);

    React.useEffect(() => {
        resizeToContent();
    }, [resizeToContent]);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = () => {
        resizeToContent();
    };

    const [value, setValue] = React.useState("");
    const displayValue = externalValue ?? value;

    const handleClick = () => {
        const prompt = displayValue.trim();
        if (!prompt) return;

        const chatUrl = new URL("https://home.atlassian.com/chat");
        chatUrl.searchParams.set("rovoChatPathway", "chat");
        chatUrl.searchParams.set("rovoChatPrompt", prompt);

        const loginUrl = `https://id.atlassian.com/login?continue=${encodeURIComponent(
            chatUrl.toString()
        )}`;

        console.log("generated URL: ", loginUrl);
        window.location.assign(loginUrl);
    };

    // Determine which button to show based on feature gate
    const usePillButton = checkFeatureFlagGate(
        featureFlags.gwp8979RovoOnWacButton
    );

    return (
        <div css={textfieldContainerStyles}>
            <textarea
                ref={textareaRef}
                css={inputStyles}
                placeholder={placeholder}
                maxLength={1000}
                value={displayValue}
                onChange={(e) => {
                    setValue(e.target.value);
                    onValueChange?.(e.target.value);
                    handleChange(e);
                }}
            />
            <div css={ctaButtonStyles}>
                {usePillButton ? (
                    <RovoPillButton
                        isDisabled={!displayValue.trim()}
                        onClick={handleClick}
                    />
                ) : (
                    <RovoIconButton
                        isDisabled={!displayValue.trim()}
                        onClick={handleClick}
                        icon={<ArrowUpIcon label="Send message" />}
                        ariaLabel="Send message"
                    />
                )}
            </div>
        </div>
    );
};

export const RovoChatBoxContainer: React.FC = () => {
    const [chatValue, setChatValue] = React.useState("");
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const handleColdStartButtonClick = (prompt: string) => {
        setChatValue(prompt);
    };

    return (
        <div css={chatBoxContainerStyles}>
            <RovoChatBox
                textareaRef={textareaRef}
                value={chatValue}
                onValueChange={setChatValue}
            />
            <ColdStartButtonsRow onButtonClick={handleColdStartButtonClick} />
            <AIFooter />
        </div>
    );
};
/**
 * RovoChat component displays a welcoming header for the Rovo chat experience - will add other components of the chat box in future PRs
 */
export const RovoChat: React.FC = () => {
    return (
        <div css={sectionStyles}>
            <ChatHeader />
            <RovoChatBoxContainer />
        </div>
    );
};
